import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose, newFormatPrice } from '../../../../services/utilityFunctions';
import { formatNumber } from '../../../../services/numberUtilis';
import withCSRF from '../../../../commonComponents/withCSRF';
import ModalHeader from '../../../../commonComponents/modal/ModalHeader';
import Modal from '../../../../commonComponents/modal/Modal';
import translate from '../../../../commonComponents/translate';
import FormInput from '../../../../commonComponents/forms/FormInput';
import FormLabel from '../../../../commonComponents/forms/FormLabel';
import ModalFooter from '../../../../commonComponents/modal/ModalFooter';
import walletProp, { WALLET_TYPE } from '../../proptype/walletProp';
import Notifications from '../../../../services/notifications';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import WalletIcon from '../wallet-picker/icons/WalletIcon';
import CoinsIcon from '../wallet-picker/icons/CoinsIcon';

const apiHeaders = csrf => ({
  'Accept-Version': '1.0.0',
  'X-Gamification-API': 'Games',
  'X-CSRF-Token': csrf,
  _csrf: csrf,
});

function ExchangeModal({ t, _csrf, activeWallet, wallets, onClose }) {
  const { organization } = useContext(ConfigsContext);
  const { walletConfiguration } =
    organization?.services?.wallet_advanced?.config || {};

  const { type, currencyInfo } = activeWallet;
  const isCrypto = type === WALLET_TYPE.CRYPTO;
  const isCoins = type === WALLET_TYPE.COINS;

  const allowedConversions = useMemo(
    () => activeWallet?.allowedConversions || [],
    [activeWallet],
  );

  const fromCurrency = isCrypto
    ? activeWallet?.currencyInfo?.code
    : activeWallet?.currency;

  const sourceAvailable = useMemo(() => {
    if (isCoins) {
      return Number(activeWallet.coinsAvailable || activeWallet.available || 0);
    }
    return Number(activeWallet.available || 0);
  }, [activeWallet, isCoins]);

  const cryptoSymbol =
    currencyInfo?.symbol_native || currencyInfo?.symbol || '';

  const walletName = walletType => {
    if (walletType === WALLET_TYPE.CRYPTO) {
      const cryptoWallet = wallets.find(w => w.type === WALLET_TYPE.CRYPTO);
      return (
        cryptoWallet?.currencyInfo?.name ||
        t('commissions.walletType.crypto')
      );
    }
    return (
      walletConfiguration?.[walletType]?.name ||
      t(`commissions.walletType.${walletType}`)
    );
  };

  const renderIcon = (walletType, cInfo) => {
    const customIcon = walletConfiguration?.[walletType]?.icon?.url;
    switch (walletType) {
      case WALLET_TYPE.CRYPTO:
        return cInfo?.icon ? (
          <img src={cInfo.icon} alt="crypto logo" />
        ) : (
          <WalletIcon />
        );
      case WALLET_TYPE.COINS:
        return customIcon ? (
          <img src={customIcon} alt="coins logo" />
        ) : (
          <CoinsIcon />
        );
      default:
        return customIcon ? (
          <img src={customIcon} alt="wallet logo" />
        ) : (
          <WalletIcon />
        );
    }
  };

  const currencyForType = walletType => {
    if (walletType === WALLET_TYPE.COINS) return undefined;
    const w = wallets.find(item => item.type === walletType);
    if (walletType === WALLET_TYPE.CRYPTO) return w?.currencyInfo?.code;
    return w?.currency;
  };

  const availableDisplay = useMemo(() => {
    if (isCrypto) return `${sourceAvailable}${cryptoSymbol}`;
    if (isCoins) return formatNumber(sourceAvailable);
    return newFormatPrice(
      sourceAvailable,
      true,
      (fromCurrency || '').toUpperCase(),
    );
  }, [isCrypto, isCoins, sourceAvailable, cryptoSymbol, fromCurrency]);

  const [target, setTarget] = useState(allowedConversions[0] || '');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const amountNumber = parseFloat(amount);
  const amountIsValid =
    !Number.isNaN(amountNumber) &&
    amountNumber > 0 &&
    amountNumber <= sourceAvailable;

  const amountError = useMemo(() => {
    if (amount === '') return '';
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      return t('commissions.exchangeModal.amountZero');
    }
    if (amountNumber > sourceAvailable) {
      return t('commissions.exchangeModal.amountOverBalance');
    }
    return '';
  }, [amount, amountNumber, sourceAvailable, t]);

  // Debounced live quote.
  useEffect(() => {
    setQuote(null);
    if (!target || !amountIsValid) return undefined;

    const handle = setTimeout(async () => {
      try {
        setIsQuoting(true);
        setError('');
        const { data } = await axios.post(
          '/api/wallet/exchange/quote',
          {
            from: type,
            to: target,
            amount: amountNumber,
            fromCurrency,
            toCurrency: currencyForType(target),
          },
          { headers: apiHeaders(_csrf) },
        );
        setQuote(data?.data || null);
      } catch (err) {
        setQuote(null);
        setError(t('commissions.exchangeModal.quoteError'));
      } finally {
        setIsQuoting(false);
      }
    }, 500);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, amount]);

  const handleConfirm = async () => {
    if (!amountIsValid || !target) return;
    try {
      setIsSubmitting(true);
      await axios.post(
        '/api/wallet/exchange',
        {
          from: type,
          to: target,
          amount: amountNumber,
          fromCurrency,
          toCurrency: currencyForType(target),
        },
        { headers: apiHeaders(_csrf) },
      );
      setIsSubmitting(false);
      onClose(true);
      Notifications.showNotificationSuccess(
        t('success'),
        t('commissions.exchange.success'),
      );
    } catch (err) {
      setIsSubmitting(false);
      Notifications.showErrorFromRequest(err);
    }
  };

  const sourceIconWrapperClass = isCoins
    ? 'withdrawal-modal-banner-wallet-value-icon withdrawal-modal-banner-coins'
    : 'withdrawal-modal-banner-wallet-value-icon';

  return (
    <Modal id="exchangeModal" onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader
            title={t('commissions.exchangeModal.title')}
            onClose={() => onClose(null)}
          />
          <div className="modal-body">
            <div className="container-fluid">
              <div className="portlet-body form">
                <div className="withdrawal-modal-banner">
                  <div className="withdrawal-modal-banner-wallet">
                    <div className="withdrawal-modal-banner-wallet-label">
                      {t('commissions.exchangeModal.from')}
                    </div>
                    <div className="withdrawal-modal-banner-wallet-value">
                      <div className={sourceIconWrapperClass}>
                        {renderIcon(type, currencyInfo)}
                      </div>
                      <div className="withdrawal-modal-banner-wallet-value-name">
                        {walletName(type)}
                      </div>
                    </div>
                  </div>
                  <div className="withdrawal-modal-banner-available">
                    <div className="withdrawal-modal-banner-available-label">
                      {t('commissions.available')}
                    </div>
                    <div className="withdrawal-modal-banner-available-value">
                      {availableDisplay}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="form-group m-form__group row">
                      <FormLabel
                        id="exchangeTarget"
                        label={`${t('commissions.exchangeModal.to')}*`}
                        className="col-lg-2 col-12"
                      />
                      <div className="col-lg-10 col-12">
                        <div className="network-container">
                          {allowedConversions.map(targetType => {
                            const w = wallets.find(
                              item => item.type === targetType,
                            );
                            return (
                              <button
                                type="button"
                                key={targetType}
                                className="network-item"
                                onClick={() => setTarget(targetType)}
                              >
                                <div
                                  className={`network-item-radio ${
                                    target === targetType ? 'selected' : ''
                                  }`}
                                />
                                {renderIcon(targetType, w?.currencyInfo)}
                                {walletName(targetType)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div
                      className={`form-group m-form__group row ${
                        amountError ? 'has-danger' : ''
                      }`}
                    >
                      <FormLabel
                        id="exchangeAmount"
                        label={t('commissions.exchangeModal.amount')}
                        className="col-lg-2 col-12"
                      />
                      <FormInput
                        id="exchangeAmount"
                        type="number"
                        name="amount"
                        containerClassName="col-lg-4 col-12"
                        groupProps={{ skipWrapper: true }}
                        value={amount}
                        onChange={value => setAmount(value)}
                        error={amountError}
                        inputProps={{ min: 0, max: sourceAvailable, step: 'any' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="exchange-modal__quote">
                  {isQuoting && (
                    <span>{t('commissions.exchangeModal.calculating')}</span>
                  )}
                  {!isQuoting && error && (
                    <span className="exchange-modal__quote-error">{error}</span>
                  )}
                  {!isQuoting && quote && (
                    <>
                      <div className="exchange-modal__quote-row">
                        <span>{t('commissions.exchangeModal.rate')}</span>
                        <span>{quote.rate}</span>
                      </div>
                      {Number(quote.feePct) > 0 && (
                        <div className="exchange-modal__quote-row">
                          <span>{t('commissions.exchangeModal.fee')}</span>
                          <span>
                            {quote.feeAmount} ({quote.feePct}%)
                          </span>
                        </div>
                      )}
                      <div className="exchange-modal__quote-row exchange-modal__quote-row--total">
                        <span>{t('commissions.exchangeModal.youGet')}</span>
                        <span>
                          {target === WALLET_TYPE.COINS ? (
                            <>
                              {formatNumber(quote.targetAmount)}{' '}
                              {t('commissions.walletType.coins')}
                              {quote.targetAmountFiat != null && (
                                <>
                                  {' '}
                                  (≈{' '}
                                  {newFormatPrice(
                                    quote.targetAmountFiat,
                                    false,
                                    (quote.targetFiatCurrency || '').toUpperCase(),
                                  )}
                                  )
                                </>
                              )}
                            </>
                          ) : target === WALLET_TYPE.FIAT ? (
                            newFormatPrice(
                              quote.targetAmount,
                              false,
                              (quote.targetCurrency || '').toUpperCase(),
                            )
                          ) : (
                            <>
                              {quote.targetAmount}
                              {wallets.find(w => w.type === target)?.currencyInfo
                                ?.symbol_native ||
                                wallets.find(w => w.type === target)
                                  ?.currencyInfo?.symbol ||
                                ` ${(quote.targetCurrency || '').toUpperCase()}`}
                            </>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <ModalFooter
            saveText={t('commissions.exchangeModal.confirm')}
            onSave={handleConfirm}
            disabled={isSubmitting || isQuoting || !quote || !amountIsValid}
            saveIsLoader={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
}

ExchangeModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  activeWallet: PropTypes.shape(walletProp.propType).isRequired,
  wallets: PropTypes.arrayOf(PropTypes.shape(walletProp.propType)).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default compose(translate, withCSRF)(ExchangeModal);
